<script lang="ts">
  import { useTimeout } from '$lib/hooks/useTimer'
  import type { BatchProgress } from '../services/batchCheckService'
  
  interface Props {
    message: string | null
    type?: 'info' | 'success' | 'error' | 'warning' | 'batch'
    onClose?: () => void
    progress?: BatchProgress | null
  }
  
  let { message, type = 'info', onClose, progress = null }: Props = $props()
  let isVisible = $state(false)
  
  // Fade out timer - starts automatically when message appears
  // Don't auto-hide if it's a batch progress message
  const fadeOutTimer = useTimeout(() => {
    if (type !== 'batch' || !progress) {
      isVisible = false
    }
  }, 3300, false)
  
  // Close animation timer
  const closeAnimationTimer = useTimeout(() => {
    onClose?.()
  }, 300, false)
  
  $effect(() => {
    if (message) {
      isVisible = true
      // Start fade out 200ms before the message disappears
      fadeOutTimer.restart()
    }
  })
  
  function getTypeClass(): string {
    switch (type) {
      case 'success':
        return 'status-success'
      case 'error':
        return 'status-error'
      case 'warning':
        return 'status-warning'
      case 'batch':
        return 'status-batch'
      default:
        return 'status-info'
    }
  }
  
  function handleClose() {
    isVisible = false
    fadeOutTimer.stop()
    closeAnimationTimer.start() // Wait for fade animation
  }
</script>

{#if message}
  <div class="status-message-overlay {!isVisible ? 'fade-out' : ''}">
    <div class="status-message {getTypeClass()}">
      <span class="message-text">
        {message}
      </span>
      
      {#if type === 'batch' && progress && progress.totalChunks > 1}
        <div class="batch-progress">
          <div class="progress-bar-mini">
            <div 
              class="progress-bar-fill-mini"
              style="width: {progress.percentComplete}%"
            ></div>
          </div>
          <span class="progress-text">{progress.percentComplete}%</span>
        </div>
      {/if}
      
      <button class="close-btn" onclick={handleClose} aria-label="닫기">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
    </div>
  </div>
{/if}

<style>
  .status-message-overlay {
    position: fixed;
    bottom: 60px; /* Raised position */
    left: 50%;
    transform: translateX(-50%);
    z-index: 9999; /* Below modal and buttons */
    pointer-events: none; /* Allow clicks to pass through overlay */
    animation: fadeInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
  
  .status-message-overlay.fade-out {
    animation: fadeOutDown 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
  
  @keyframes fadeOutDown {
    from {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
    to {
      opacity: 0;
      transform: translateX(-50%) translateY(10px);
    }
  }
  
  .status-message {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 36px 10px 16px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: 1px solid;
    letter-spacing: -0.01em;
    position: relative;
    pointer-events: auto;
  }
  
  .message-text {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  /* Close button */
  .close-btn {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    padding: 2px;
    cursor: pointer;
    color: currentColor;
    opacity: 0.4;
    transition: opacity 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
  }
  
  .close-btn:hover {
    opacity: 0.7;
    background: rgba(0, 0, 0, 0.05);
  }
  
  .close-btn:active {
    opacity: 1;
  }
  
  
  /* Light mode colors */
  .status-info {
    background: white;
    color: #374151;
    border-color: #e5e7eb;
  }
  
  .status-success {
    background: #f0fdf4;
    color: #059669;
    border-color: #86efac;
  }
  
  .status-error {
    background: #fef2f2;
    color: #dc2626;
    border-color: #fca5a5;
  }
  
  .status-warning {
    background: #fffbeb;
    color: #d97706;
    border-color: #fcd34d;
  }
  
  .status-batch {
    background: #f9fafb;
    color: #4b5563;
    border-color: #d1d5db;
  }
  
  .batch-progress {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-left: 8px;
  }
  
  .progress-bar-mini {
    width: 60px;
    height: 3px;
    background: #e5e7eb;
    border-radius: 2px;
    overflow: hidden;
  }
  
  .progress-bar-fill-mini {
    height: 100%;
    background: #6b7280;
    transition: width 0.3s ease;
  }
  
  .progress-text {
    font-size: 11px;
    font-weight: 500;
    color: #6b7280;
    min-width: 28px;
  }
  
  /* Dark mode colors */
  @media (prefers-color-scheme: dark) {
    .status-message {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
    }
    
    .status-info {
      background: #374151;
      color: #f3f4f6;
      border-color: #4b5563;
    }
    
    .status-success {
      background: #064e3b;
      color: #6ee7b7;
      border-color: #047857;
    }
    
    .status-error {
      background: #7f1d1d;
      color: #fca5a5;
      border-color: #991b1b;
    }
    
    .status-warning {
      background: #78350f;
      color: #fcd34d;
      border-color: #92400e;
    }
    
    .status-batch {
      background: #1f2937;
      color: #d1d5db;
      border-color: #374151;
    }
    
    .progress-bar-mini {
      background: #374151;
    }
    
    .progress-bar-fill-mini {
      background: #9ca3af;
    }
    
    .progress-text {
      color: #d1d5db;
    }
    
    .close-btn:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  }
  
  :global(.dark-theme) .status-message {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  }
  
  :global(.dark-theme) .status-info {
    background: #374151;
    color: #f3f4f6;
    border-color: #4b5563;
  }
  
  :global(.dark-theme) .status-success {
    background: #064e3b;
    color: #6ee7b7;
    border-color: #047857;
  }
  
  :global(.dark-theme) .status-error {
    background: #7f1d1d;
    color: #fca5a5;
    border-color: #991b1b;
  }
  
  :global(.dark-theme) .status-warning {
    background: #78350f;
    color: #fcd34d;
    border-color: #92400e;
  }
  
  :global(.dark-theme) .status-batch {
    background: #1f2937;
    color: #d1d5db;
    border-color: #374151;
  }
  
  :global(.dark-theme) .progress-bar-mini {
    background: #374151;
  }
  
  :global(.dark-theme) .progress-bar-fill-mini {
    background: #9ca3af;
  }
  
  :global(.dark-theme) .progress-text {
    color: #d1d5db;
  }
  
  :global(.dark-theme) .close-btn:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  /* Mobile responsive */
  @media (max-width: 768px) {
    .status-message-overlay {
      bottom: 80px; /* Adjusted for mobile */
    }
    
    .status-message {
      font-size: 12px;
      padding: 6px 14px;
    }
    
    .status-message::before {
      width: 5px;
      height: 5px;
    }
  }
</style>